import type {
  SpecType,
  SpecsValidationIssue,
} from "../../service/SpecsRevisionService";

const SHOW_WARNINGS = false;

export function filterDisplayedIssues(
  issues: SpecsValidationIssue[]
): SpecsValidationIssue[] {
  if (SHOW_WARNINGS) {
    return issues;
  }
  return issues.filter((issue) => getSpecsIssueSeverity(issue) !== "warning");
}

export type SpecsValidationIssueMatch = {
  severity: "error" | "warning";
  messages: string[];
  targetPaths: string[];
};

const rootModelPathBySpecType: Partial<Record<SpecType, string>> = {
  family: "family",
  fabric: "fabric",
  decoration: "decoration",
};

const collectionPathBySpecType: Record<SpecType, string> = {
  family: "families",
  fabric: "fabrics",
  decoration: "decorations",
  product: "products",
};

function uniqueValues(values: string[]) {
  return [...new Set(values)];
}

function isNumericSegment(segment: string) {
  return /^\d+$/.test(segment);
}

function parsePathString(path: string): Array<string | number> {
  return path
    .replace(/\[(\d+)\]/g, ".$1")
    .replace(/\s*>\s*/g, ".")
    .split(".")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => (isNumericSegment(segment) ? Number(segment) : segment));
}

function normalizeIssuePath(
  path: SpecsValidationIssue["path"]
): Array<string | number> {
  if (!path) {
    return [];
  }

  if (typeof path === "string") {
    return parsePathString(path);
  }

  return path;
}

function normalizeIssueFields(fields: SpecsValidationIssue["fields"]) {
  return Array.isArray(fields)
    ? fields.filter((field) => field.trim().length > 0)
    : [];
}

function stripEntityCollectionPrefix(
  specType: SpecType,
  segments: Array<string | number>
) {
  const collectionPath = collectionPathBySpecType[specType];
  if (segments[0] === collectionPath && typeof segments[1] === "number") {
    return segments.slice(2);
  }

  return segments;
}

function addModelRootPrefix(
  specType: SpecType,
  segments: Array<string | number>
) {
  const rootPath = rootModelPathBySpecType[specType];
  if (!rootPath) {
    return stripEntityCollectionPrefix(specType, segments);
  }

  if (segments[0] === rootPath) {
    return segments;
  }

  return [rootPath, ...stripEntityCollectionPrefix(specType, segments)];
}

function appendFieldSegments(
  baseSegments: Array<string | number>,
  fieldPath: string
) {
  return [...baseSegments, ...parsePathString(fieldPath)];
}

function formatSegmentsForDisplay(segments: Array<string | number>): string {
  return segments.reduce<string>((path, segment) => {
    if (typeof segment === "number") {
      return `${path}[${segment}]`;
    }

    return path ? `${path}.${segment}` : segment;
  }, "");
}

function formatSegmentsForModelPath(segments: Array<string | number>): string {
  return segments.map(String).join(".");
}

export function getSpecsIssueSeverity(issue: SpecsValidationIssue) {
  return issue.severity === "warning" ? "warning" : "error";
}

export function getSpecsIssueDisplayTargets(issue: SpecsValidationIssue) {
  const baseSegments = normalizeIssuePath(issue.path);
  const fields = normalizeIssueFields(issue.fields);
  const targetSegments =
    fields.length > 0
      ? fields.map((field) => appendFieldSegments(baseSegments, field))
      : [baseSegments];

  return uniqueValues(
    targetSegments
      .filter((segments) => segments.length > 0)
      .map(formatSegmentsForDisplay)
  );
}

export function getSpecsIssueModelTargets(
  specType: SpecType,
  issue: SpecsValidationIssue
) {
  const rawBaseSegments = normalizeIssuePath(issue.path);
  const fields = normalizeIssueFields(issue.fields);
  if (rawBaseSegments.length === 0 && fields.length === 0) {
    return [];
  }

  const baseSegments = addModelRootPrefix(specType, rawBaseSegments);
  const targetSegments =
    fields.length > 0
      ? fields.map((field) => appendFieldSegments(baseSegments, field))
      : [baseSegments];

  return uniqueValues(
    targetSegments
      .filter((segments) => segments.length > 0)
      .map(formatSegmentsForModelPath)
  );
}

export function formatSpecsIssueLocation(issue: SpecsValidationIssue) {
  const targets = getSpecsIssueDisplayTargets(issue);
  if (targets.length > 0) {
    return targets.join(", ");
  }

  if (issue.validator) {
    return issue.validator;
  }

  return "Unknown field";
}

function modelPathMatchesTarget(
  modelPath: string,
  targetPath: string,
  includeAncestorTargets: boolean
) {
  return (
    targetPath === modelPath ||
    targetPath.startsWith(`${modelPath}.`) ||
    (includeAncestorTargets && modelPath.startsWith(`${targetPath}.`))
  );
}

export function getSpecsIssueMatchForModelPath({
  issues,
  specType,
  modelPath,
  includeAncestorTargets = false,
}: {
  issues: SpecsValidationIssue[];
  specType: SpecType;
  modelPath: string;
  includeAncestorTargets?: boolean;
}): SpecsValidationIssueMatch | null {
  const matched = filterDisplayedIssues(issues)
    .map((issue) => ({
      issue,
      targetPaths: getSpecsIssueModelTargets(specType, issue),
    }))
    .filter(({ targetPaths }) =>
      targetPaths.some((targetPath) =>
        modelPathMatchesTarget(modelPath, targetPath, includeAncestorTargets)
      )
    );

  if (matched.length === 0) {
    return null;
  }

  const severity = matched.some(
    ({ issue }) => getSpecsIssueSeverity(issue) === "error"
  )
    ? "error"
    : "warning";

  return {
    severity,
    messages: uniqueValues(matched.map(({ issue }) => issue.message)),
    targetPaths: uniqueValues(
      matched.flatMap(({ targetPaths }) => targetPaths)
    ),
  };
}

export function summarizeSpecsValidationIssues(
  issues: SpecsValidationIssue[],
  limit = 6
) {
  const summaries = issues.slice(0, limit).map((issue) => ({
    severity: getSpecsIssueSeverity(issue),
    location: formatSpecsIssueLocation(issue),
    message: issue.message,
  }));

  return {
    summaries,
    hiddenCount: Math.max(issues.length - summaries.length, 0),
  };
}
